import React from 'react'
import { ApiError, Service, ValidationError, api } from '../../api/client'
import type { Discount } from '../../types/app'
import Field from './Field'
import LengthChecklist from './LengthChecklist'
import { useDialog } from '../../hooks/useDialog'
import {
  OfferForm,
  emptyOfferForm,
  lockedForm,
  formFromOffer,
  toDiscountInput,
  validateOfferForm,
} from './offerForm'
import { saveErrorMessage } from './toast'
import '../../components/Sheet.css'
import './Settings.css'

const IN_USE_NOTE = 'Bookings already use this offer, so only its name can change.'

interface OfferSheetProps {
  offer: Discount | null
  services: Service[]
  onClose: () => void
  onSaved: (message: string) => void
}

function today(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default function OfferSheet({ offer, services, onClose, onSaved }: OfferSheetProps) {
  const [form, setForm] = React.useState<OfferForm>(() => (offer ? formFromOffer(offer) : emptyOfferForm(today())))
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState(false)
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })
  const [error, setError] = React.useState('')
  const [inUse, setInUse] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)

  // Once bookings carry an offer its terms are fixed: only the name can change.
  const locked = inUse || (offer?.uses ?? 0) > 0
  const isCode = form.type === 'code'

  const set = <K extends keyof OfferForm>(key: K, value: OfferForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const toggleLength = (lengthId: string) =>
    set(
      'lengthIds',
      form.lengthIds.includes(lengthId)
        ? form.lengthIds.filter((id) => id !== lengthId)
        : [...form.lengthIds, lengthId],
    )

  const fail = (err: unknown) => {
    if (err instanceof ValidationError) {
      setErrors(err.fields)
    } else if (err instanceof ApiError && err.problem === 'discount-in-use') {
      // The server counts cancelled bookings too, so the sheet may have
      // looked editable. Drop her edited terms; keep the name she typed.
      if (offer) {
        setForm((current) => lockedForm(offer, current.name))
      }
      setErrors({})
      setInUse(true)
    } else {
      setError(saveErrorMessage(err))
    }
    setBusy(false)
  }

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    const found = validateOfferForm(form, locked, services)
    if (Object.keys(found).length > 0) {
      setErrors(found)
      return
    }
    const input = toDiscountInput(form, services)
    setBusy(true)
    setErrors({})
    setError('')
    try {
      if (offer) {
        await api.updateDiscount(offer.id, input)
        onSaved(`${input.name} saved.`)
      } else {
        await api.createDiscount(input)
        onSaved(`${input.name} added.`)
      }
    } catch (err) {
      fail(err)
    }
  }

  const endNow = async () => {
    if (!offer) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.endDiscount(offer.id)
      onSaved(`${offer.name} ended.`)
    } catch (err) {
      fail(err)
    }
  }

  const remove = async () => {
    if (!offer) {
      return
    }
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.deleteDiscount(offer.id)
      onSaved(`${offer.name} deleted.`)
    } catch (err) {
      fail(err)
    }
  }

  return (
    <div className="sheet-overlay settings-sheet" onClick={() => { if (!busy) onClose() }}>
      <div
        ref={dialogRef}
        className="sheet-content"
        role="dialog"
        aria-modal="true"
        aria-label={offer ? `Edit ${offer.name}` : 'Add an offer'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>{offer ? 'Edit offer' : 'New offer'}</h2>
        </div>

        <form className="settings-form sheet-form" onSubmit={save} noValidate>
          {locked && <p className="field-hint">{IN_USE_NOTE}</p>}

          <Field id="offer-name" label="Name" error={errors.name}>
            <input id="offer-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>

          <Field id="offer-type" label="Type" error={errors.kind}>
            <select
              id="offer-type"
              value={form.type}
              disabled={locked}
              onChange={(e) => set('type', e.target.value as OfferForm['type'])}
            >
              <option value="sale">Sale (applies itself)</option>
              <option value="code">Code (clients type it)</option>
            </select>
          </Field>

          {isCode && (
            <Field id="offer-code" label="Code" error={errors.code} hint="Letters and numbers, like AUTUMN10.">
              <input
                id="offer-code"
                autoCapitalize="characters"
                value={form.code}
                disabled={locked}
                onChange={(e) => set('code', e.target.value)}
              />
            </Field>
          )}

          <div className="field-pair">
            <Field id="offer-unit" label="Amount off">
              <select
                id="offer-unit"
                value={form.unit}
                disabled={locked}
                onChange={(e) => set('unit', e.target.value as OfferForm['unit'])}
              >
                <option value="percent">Percent (%)</option>
                <option value="amount">Pounds (£)</option>
              </select>
            </Field>
            <Field id="offer-value" label={form.unit === 'percent' ? 'Percent' : 'Pounds'} error={errors.value}>
              <input
                id="offer-value"
                inputMode="decimal"
                placeholder={form.unit === 'percent' ? '20' : '10.00'}
                value={form.value}
                disabled={locked}
                onChange={(e) => set('value', e.target.value)}
              />
            </Field>
          </div>

          <div className="field-pair">
            <Field id="offer-starts" label="Bookings made from" error={errors.starts_on}>
              <input
                id="offer-starts"
                type="date"
                value={form.startsOn}
                disabled={locked}
                onChange={(e) => set('startsOn', e.target.value)}
              />
            </Field>
            <Field id="offer-ends" label={isCode ? 'Until (optional)' : 'Until'} error={errors.ends_on}>
              <input
                id="offer-ends"
                type="date"
                value={form.endsOn}
                disabled={locked}
                onChange={(e) => set('endsOn', e.target.value)}
              />
            </Field>
          </div>

          <Field id="offer-services" label="Treatments" error={errors.service_ids ?? errors.service_length_ids}>
            <select
              id="offer-services"
              value={form.allServices ? 'all' : 'chosen'}
              disabled={locked}
              onChange={(e) => set('allServices', e.target.value === 'all')}
            >
              <option value="all">All treatments</option>
              <option value="chosen">Chosen treatments</option>
            </select>
            {!form.allServices && (
              <LengthChecklist
                id="offer-lengths"
                label="Treatments"
                services={services}
                ticked={form.lengthIds}
                disabled={locked}
                onToggle={toggleLength}
              />
            )}
          </Field>

          {isCode && (
            <>
              <Field id="offer-max" label="Limit on uses (optional)" error={errors.max_uses}>
                <input
                  id="offer-max"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={form.maxUses}
                  disabled={locked}
                  onChange={(e) => set('maxUses', e.target.value)}
                />
              </Field>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={form.oncePerClient}
                  disabled={locked}
                  onChange={(e) => set('oncePerClient', e.target.checked)}
                />{' '}
                Once per client
              </label>
              {errors.once_per_client && <p className="field-error" role="alert">{errors.once_per_client}</p>}
            </>
          )}

          {error && <p className="field-error" role="alert">{error}</p>}

          <div className="sheet-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>

          {offer && offer.state !== 'ended' && (
            <button type="button" className="btn btn-secondary btn-compact" onClick={endNow} disabled={busy}>
              End now
            </button>
          )}
          {offer && (
            <button type="button" className="btn btn-danger btn-compact" onClick={remove} disabled={busy}>
              {confirmDelete ? `Tap again to delete ${offer.name}` : 'Delete offer'}
            </button>
          )}
        </form>
      </div>
    </div>
  )
}
